import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchAllocations,
  fetchGlPreview,
  fetchProjects,
  fetchUsers,
  replaceAllocations,
} from '../api';

// Statuses where allocations are visible. Mirrors the server-side
// `VISIBLE_STATUSES` set in allocation_service.py — kept in sync by hand
// because pulling it over the wire for every render would be wasteful.
const VISIBLE_STATUSES = new Set([
  'pending_approval',
  'approved',
  'submitted_to_ap',
  'payment_processing',
  'posted_in_sap',
  'reconciled',
  'vat_open',
  'vat_batched',
  'vat_submitted',
  'vat_paid',
  'lux_open',
  'lux_batched',
  'lux_submitted',
  'lux_paid',
]);

function toAmountString(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return String(value);
}

function parseAmount(input) {
  if (input == null || input === '') return null;
  const num = Number(input);
  if (Number.isNaN(num)) return null;
  return Math.round(num * 100) / 100;
}

function formatCurrency(amount, currency) {
  if (amount == null) return '—';
  const num = typeof amount === 'number' ? amount : parseFloat(amount);
  if (Number.isNaN(num)) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency || ''}`.trim();
  }
}

function emptyDraft(projectId = '') {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    project_id: projectId,
    amount: '',
    gl_company_code: '',
    gl_cost_center: '',
    gl_internal_order: '',
    gl_account: '',
    memo: '',
  };
}

const cellStyle = {
  width: '100%',
  padding: '4px 6px',
  fontSize: 12,
  border: '1px solid #ddd',
  borderRadius: 4,
  background: '#fff',
  boxSizing: 'border-box',
};

function rowFromServer(row) {
  return {
    key: row.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    project_id: row.project_id || '',
    amount: toAmountString(row.amount),
    gl_company_code: row.gl_company_code || '',
    gl_cost_center: row.gl_cost_center || '',
    gl_internal_order: row.gl_internal_order || '',
    gl_account: row.gl_account || '',
    memo: row.memo || '',
  };
}

function AllocationsPanel({ invoiceId, invoiceStatus }) {
  const [meta, setMeta] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userNamesById, setUserNamesById] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [dirty, setDirty] = useState(false);
  // Default collapsed so the panel doesn't crowd the page when nothing
  // interesting is happening (the common 1-allocation case). Once we know
  // the invoice is actually split (>1 row) we auto-expand on load — the
  // user is already looking at a card that says "split: N", they should
  // see the split when they open it. Manual toggle still works either way.
  const [collapsed, setCollapsed] = useState(true);

  const visible = VISIBLE_STATUSES.has(invoiceStatus);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const [allocRes, projectsRes, usersRes] = await Promise.all([
        fetchAllocations(invoiceId),
        fetchProjects(),
        fetchUsers(),
      ]);
      const data = allocRes.data;
      setMeta(data);
      setProjects(projectsRes.data || []);
      const namesById = {};
      for (const u of usersRes.data || []) {
        namesById[u.id] = u.name;
      }
      setUserNamesById(namesById);
      const rows = (data.allocations || []).map(rowFromServer);
      setDrafts(rows);
      setDirty(false);
      if (rows.length > 1) {
        setCollapsed(false);
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to load allocations',
      });
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    if (visible) {
      load();
    } else {
      setLoading(false);
    }
  }, [visible, load]);

  const editable = meta?.editable === true;
  const invoiceTotal = meta?.invoice_total != null ? Number(meta.invoice_total) : null;
  const currency = meta?.invoice_currency || 'USD';

  const allocatedTotal = useMemo(() => {
    return drafts.reduce((acc, row) => {
      const v = parseAmount(row.amount);
      return v != null ? acc + v : acc;
    }, 0);
  }, [drafts]);

  const remainder = invoiceTotal != null
    ? Math.round((invoiceTotal - allocatedTotal) * 100) / 100
    : null;
  const balanced = remainder != null && Math.abs(remainder) <= 0.01;

  const handleAddRow = () => {
    setDrafts((prev) => [...prev, emptyDraft()]);
    setDirty(true);
  };

  const handleRemoveRow = (key) => {
    if (drafts.length <= 1) return;
    setDrafts((prev) => prev.filter((d) => d.key !== key));
    setDirty(true);
  };

  const handleFieldChange = (key, field, value) => {
    setDrafts((prev) => prev.map((d) =>
      d.key === key ? { ...d, [field]: value } : d
    ));
    setDirty(true);
  };

  const handleProjectChange = async (key, projectId) => {
    handleFieldChange(key, 'project_id', projectId);
    if (!projectId) return;
    // Pull the full GL coding cascade for the new project so all four
    // codes populate (project_vendor mapping → vendor default → project
    // default). The /gl-preview endpoint auto-uses the invoice's vendor
    // when we don't pass one.
    try {
      const res = await fetchGlPreview(invoiceId, projectId);
      const proposed = res.data?.proposed || {};
      const pick = (field) => proposed[field]?.value || '';
      setDrafts((prev) => prev.map((d) =>
        d.key === key
          ? {
              ...d,
              gl_company_code: pick('gl_company_code'),
              gl_cost_center: pick('gl_cost_center'),
              gl_internal_order: pick('gl_internal_order'),
              gl_account: pick('gl_account'),
            }
          : d
      ));
    } catch {
      // Non-fatal: leave GL fields untouched and let the user fill in.
    }
  };

  const handleSave = async () => {
    if (!balanced) return;
    setSaving(true);
    setMessage(null);
    const payload = drafts.map((d) => ({
      project_id: d.project_id,
      amount: d.amount,
      gl_company_code: d.gl_company_code || null,
      gl_cost_center: d.gl_cost_center || null,
      gl_internal_order: d.gl_internal_order || null,
      gl_account: d.gl_account || null,
      memo: d.memo || null,
    }));
    try {
      const res = await replaceAllocations(invoiceId, payload);
      setMeta(res.data);
      const rows = (res.data.allocations || []).map(rowFromServer);
      setDrafts(rows);
      setDirty(false);
      setMessage({ type: 'success', text: 'Allocations saved' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to save allocations',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!visible) return null;
  if (loading) {
    return (
      <div className="detail-card">
        <h3 className="section-heading">Project Allocations</h3>
        <p className="empty-text">Loading allocations…</p>
      </div>
    );
  }

  const indicatorColor = balanced ? '#2e7d32' : '#c62828';
  const projectOptions = projects
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const allocationCount = drafts.length;

  const ownerNameForProject = (projectId) => {
    if (!projectId) return null;
    const project = projects.find((p) => p.id === projectId);
    if (!project || !project.owner_id) return null;
    return userNamesById[project.owner_id] || null;
  };

  return (
    <div className="detail-card" id="allocations-section">
      <h3
        className="section-heading"
        onClick={() => setCollapsed((c) => !c)}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ fontSize: '0.9em', color: '#666' }}>
          {collapsed ? '▸' : '▾'}
        </span>
        Project Allocations
        {allocationCount > 1 && (
          <span
            style={{
              fontSize: '0.75em',
              color: '#7a4500',
              background: '#fff4d6',
              border: '1px solid #f0c36d',
              padding: '1px 6px',
              borderRadius: 3,
              fontWeight: 600,
              marginLeft: 4,
            }}
          >
            split: {allocationCount}
          </span>
        )}
      </h3>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 12px',
          background: '#f5f7fa',
          borderRadius: 6,
          marginBottom: collapsed ? 0 : 12,
          fontSize: '0.95em',
        }}
      >
        <span>
          <strong>Allocated:</strong> {formatCurrency(allocatedTotal, currency)} of{' '}
          {formatCurrency(invoiceTotal, currency)}
        </span>
        <span style={{ color: indicatorColor, fontWeight: 600 }}>
          {balanced
            ? '✓ balanced'
            : `⚠ off by ${formatCurrency(Math.abs(remainder ?? 0), currency)}`}
        </span>
        {!editable && (
          <span style={{ color: '#666', fontStyle: 'italic' }}>
            (locked — invoice is past the editable window)
          </span>
        )}
      </div>

      {collapsed ? null : (
        <>
      {drafts.length === 0 && (
        <p className="empty-text" style={{ marginTop: 12 }}>
          No allocations yet.
        </p>
      )}

      {drafts.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'minmax(140px, 1.6fr) 96px 70px 80px 80px 90px minmax(80px, 1fr) 22px',
            gap: '6px 6px',
            alignItems: 'center',
            marginTop: 12,
            fontSize: 12,
          }}
        >
          <div style={{ color: '#666', fontWeight: 600 }}>Project</div>
          <div style={{ color: '#666', fontWeight: 600 }}>
            Amount ({currency})
          </div>
          <div style={{ color: '#666', fontWeight: 600 }}>Co</div>
          <div style={{ color: '#666', fontWeight: 600 }}>Cost Ctr</div>
          <div style={{ color: '#666', fontWeight: 600 }}>GL Acct</div>
          <div style={{ color: '#666', fontWeight: 600 }}>Int Order</div>
          <div style={{ color: '#666', fontWeight: 600 }}>Memo</div>
          <div></div>
          {drafts.map((row) => {
            const ownerName = ownerNameForProject(row.project_id);
            return (
            <React.Fragment key={row.key}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <select
                  value={row.project_id}
                  disabled={!editable}
                  onChange={(e) => handleProjectChange(row.key, e.target.value)}
                  style={cellStyle}
                >
                  <option value="">Select project</option>
                  {projectOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {ownerName && (
                  <span
                    style={{
                      fontSize: 10.5,
                      color: '#888',
                      fontStyle: 'italic',
                      paddingLeft: 4,
                    }}
                    title={`Project owner: ${ownerName}`}
                  >
                    Owner: {ownerName}
                  </span>
                )}
              </div>
              <input
                type="number"
                step="0.01"
                min="0"
                value={row.amount}
                disabled={!editable}
                onChange={(e) => handleFieldChange(row.key, 'amount', e.target.value)}
                style={{ ...cellStyle, textAlign: 'right' }}
              />
              <input
                type="text"
                value={row.gl_company_code}
                disabled={!editable}
                onChange={(e) =>
                  handleFieldChange(row.key, 'gl_company_code', e.target.value)
                }
                style={cellStyle}
              />
              <input
                type="text"
                value={row.gl_cost_center}
                disabled={!editable}
                onChange={(e) =>
                  handleFieldChange(row.key, 'gl_cost_center', e.target.value)
                }
                style={cellStyle}
              />
              <input
                type="text"
                value={row.gl_account}
                disabled={!editable}
                onChange={(e) =>
                  handleFieldChange(row.key, 'gl_account', e.target.value)
                }
                style={cellStyle}
              />
              <input
                type="text"
                value={row.gl_internal_order}
                disabled={!editable}
                onChange={(e) =>
                  handleFieldChange(row.key, 'gl_internal_order', e.target.value)
                }
                style={cellStyle}
              />
              <input
                type="text"
                value={row.memo}
                disabled={!editable}
                placeholder="optional"
                onChange={(e) => handleFieldChange(row.key, 'memo', e.target.value)}
                style={cellStyle}
              />
              {editable && drafts.length > 1 ? (
                <button
                  type="button"
                  onClick={() => handleRemoveRow(row.key)}
                  aria-label="Remove allocation row"
                  title="Remove row"
                  style={{
                    width: 22,
                    height: 22,
                    padding: 0,
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    background: '#fafafa',
                    color: '#888',
                    cursor: 'pointer',
                    fontSize: 14,
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              ) : (
                <div />
              )}
            </React.Fragment>
            );
          })}
        </div>
      )}

      {editable && (
        <div className="detail-actions" style={{ marginTop: 8 }}>
          <button
            type="button"
            className="btn-edit"
            onClick={handleAddRow}
            disabled={saving}
          >
            + Add allocation
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={handleSave}
            disabled={!dirty || !balanced || saving}
          >
            {saving ? 'Saving…' : 'Save allocations'}
          </button>
        </div>
      )}

      {message && (
        <p
          style={{
            marginTop: 8,
            color: message.type === 'success' ? '#2e7d32' : '#c62828',
          }}
        >
          {message.text}
        </p>
      )}
        </>
      )}
    </div>
  );
}

export default AllocationsPanel;
